import { inject } from "@angular/core";
import {
  Router,
  type ActivatedRouteSnapshot,
  type CanActivateFn,
  type UrlTree,
} from "@angular/router";

import { loginRedirectParams } from "../auth/login-redirect-params";
import { TENANT_CONTEXT } from "../auth/tenant-context.token";
import { AuthStateService } from "../services/auth-state.service";

/**
 * Refuses a session whose token belongs to a different organization than the
 * subdomain being visited — the check that stops a customer user reaching
 * another customer's portal by editing the URL.
 *
 * The comparison is against the `org_name` claim, which Auth0 sets to the
 * organization's slug. The `org_id` claim is an opaque `org_...` value and
 * cannot be compared to a hostname; an earlier version of this guard only
 * checked that `org_id` was present, which proved the user belonged to SOME
 * organization but not to THIS one.
 *
 * Davaco employees hold no organization claim and are allowed through; their
 * cross-organization access is authorized by role instead, server-side.
 *
 * This is a UI affordance. customer-portal-api re-makes the same decision from
 * the verified token on every request it serves.
 */
export const orgGuard: CanActivateFn = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
  const tenant = inject(TENANT_CONTEXT);
  const authState = inject(AuthStateService);
  const router = inject(Router);

  if (tenant.kind !== "auth0") {
    return true;
  }

  const user = authState.user();
  if (user === null) {
    // Carries the local-dev ?org= override over — see loginRedirectParams.
    return router.createUrlTree(["/login"], {
      queryParams: loginRedirectParams(route.queryParams),
    });
  }

  if (authState.isDavacoAdmin()) {
    return true;
  }

  const slug: string | null = tenant.organization;
  const orgName: string | null = user.organizationName;

  // An absent claim is a refusal, never a pass. A customer token that carries
  // no organization has no business on an organization-scoped host.
  //
  // Both refusals go to /no-access, the same screen as an account Auth0
  // refused outright: to the visitor it is one situation, "signed in, but not
  // a member of this organization", however it was detected.
  if (slug === null || orgName === null) {
    return router.createUrlTree(["/no-access"]);
  }

  return orgName.toLowerCase() === slug.toLowerCase()
    ? true
    : router.createUrlTree(["/no-access"]);
};
